import { useEffect, useState } from "react";
import { cachedPhoto, loadPhoto } from "./staff.ts";

type Resolved = { key: string | null; src: string | null };

/**
 * An employee's face as an object URL, or null while there is none to show.
 *
 * Never blocks: a tile or a chip renders with `null` — its own initials
 * fallback — on the first paint, and swaps once (and if) the fetch resolves.
 * See `loadPhoto` in `staff.ts` for the cache this reads.
 *
 * A cache hit is read during render rather than from an effect, so an id or
 * version that changes never flashes the previous employee's photo for a
 * frame before the effect below catches up.
 */
export function useEmployeePhoto(id: string | null, photoVersion: string | null): string | null {
  const key = id && photoVersion ? `${id}:${photoVersion}` : null;
  const [resolved, setResolved] = useState<Resolved>(() => ({
    key,
    src: id && photoVersion ? cachedPhoto(id, photoVersion) : null,
  }));

  if (resolved.key !== key) {
    setResolved({ key, src: id && photoVersion ? cachedPhoto(id, photoVersion) : null });
  }

  useEffect(() => {
    if (!id || !photoVersion || resolved.src) return;
    let cancelled = false;
    void loadPhoto(id, photoVersion).then((url) => {
      if (!cancelled) setResolved((prev) => (prev.key === key ? { key, src: url } : prev));
    });
    return () => {
      cancelled = true;
    };
  }, [id, photoVersion, key, resolved.src]);

  return resolved.src;
}
